import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ClipboardCheck,
  Calendar,
  FileCheck,
  History,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Lock,
  Loader2
} from 'lucide-react';

import { BACKEND_URL } from '@/lib/api';
const API = BACKEND_URL;

export default function AccountantAudit({ user }) {
  const [activeTab, setActiveTab] = useState('checklist');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [checklist, setChecklist] = useState([]);
  const [summary, setSummary] = useState(null);
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remarksDialog, setRemarksDialog] = useState({ open: false, item: null });
  const [signOffDialog, setSignOffDialog] = useState(false);
  const [overallRemarks, setOverallRemarks] = useState('');
  const [itemRemarks, setItemRemarks] = useState('');
  
  // Loading states to prevent double-click
  const [auditingItem, setAuditingItem] = useState(false);
  const [signingOff, setSigningOff] = useState(false);
  const [savingRemarks, setSavingRemarks] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchChecklist();
    fetchAuditSummary();
  }, [selectedDate]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchChecklist = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/accountant-audit/checklist/${selectedDate}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setChecklist(data.checklist || []);
        setSummary(data.summary);
        setAuditRecord(data.audit_record);
      }
    } catch (err) {
      console.error('Failed to fetch checklist:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditSummary = async () => {
    try {
      const res = await fetch(`${API}/api/accountant-audit/summary`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAuditSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/accountant-audit/history`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleItemAudit = async (item, audited, remarks = null) => {
    if (auditingItem) return; // Prevent double-click
    
    try {
      setAuditingItem(true);
      const res = await fetch(`${API}/api/accountant-audit/checklist/${selectedDate}/item`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          item_key: item.item_key,
          audited,
          remarks: remarks || item.remarks
        })
      });

      if (res.ok) {
        toast.success(audited ? 'Item marked as audited' : 'Audit mark removed');
        fetchChecklist();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update item');
      }
    } catch (err) {
      toast.error('Failed to update item');
    } finally {
      setAuditingItem(false);
    }
  };

  const handleSaveRemarks = async () => {
    if (savingRemarks) return; // Prevent double-click
    if (!remarksDialog.item) return;
    
    try {
      setSavingRemarks(true);
      await handleItemAudit(remarksDialog.item, true, itemRemarks);
      setRemarksDialog({ open: false, item: null });
      setItemRemarks('');
    } finally {
      setSavingRemarks(false);
    }
  };

  const handleSignOff = async () => {
    if (signingOff) return; // Prevent double-click
    
    try {
      setSigningOff(true);
      const res = await fetch(`${API}/api/accountant-audit/checklist/${selectedDate}/sign-off`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ overall_remarks: overallRemarks })
      });

      if (res.ok) {
        toast.success('Audit signed off successfully!');
        setSignOffDialog(false);
        setOverallRemarks('');
        fetchChecklist();
        fetchAuditSummary();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to sign off');
      }
    } catch (err) {
      toast.error('Failed to sign off');
    } finally {
      setSigningOff(false);
    }
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'missing':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <CheckCircle2 className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ok':
        return <Badge className="bg-green-100 text-green-800">OK</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800">Review</Badge>;
      case 'missing':
        return <Badge className="bg-red-100 text-red-800">Missing</Badge>;
      default:
        return <Badge variant="outline">N/A</Badge>;
    }
  };

  const isSignedOff = auditRecord?.signed_off;
  const canSignOff = summary && 
    checklist.every(item => 
      item.status === 'ok' || 
      item.status === 'not_applicable' || 
      (item.audited && item.remarks)
    );

  return (
    <div className="p-6 space-y-6" data-testid="accountant-audit">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Daily Audit Checklist</h1>
          <p className="text-slate-500 mt-1">Review and sign off on daily operations</p>
        </div>
      </div>

      {/* Summary Cards */}
      {auditSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Today's Status</p>
                  <p className="text-lg font-semibold capitalize">
                    {auditSummary.today.signed_off ? (
                      <span className="text-green-600">Signed Off</span>
                    ) : (
                      <span className="text-yellow-600">{auditSummary.today.status.replace('_', ' ')}</span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">This Month</p>
                  <p className="text-lg font-semibold">{auditSummary.month.completed} Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Audits</p>
                  <p className="text-lg font-semibold">{auditSummary.pending_dates.length} Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileCheck className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total This Month</p>
                  <p className="text-lg font-semibold">{auditSummary.month.total} Records</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="checklist" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Daily Checklist
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Audit History
          </TabsTrigger>
        </TabsList>

        {/* Daily Checklist Tab */}
        <TabsContent value="checklist" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Audit Checklist
                    {isSignedOff && (
                      <Badge className="bg-green-100 text-green-800 ml-2">
                        <Lock className="h-3 w-3 mr-1" />
                        Signed Off
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Review each item and add remarks where needed</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                    data-testid="audit-date-picker"
                  />
                  <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Summary Bar */}
                  {summary && (
                    <div className="flex gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{summary.ok} OK</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm">{summary.warning} Need Review</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-sm">{summary.missing} Missing</span>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <ClipboardCheck className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">{summary.audited}/{summary.total_items} Audited</span>
                      </div>
                    </div>
                  )}

                  {/* Checklist Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Status</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead className="w-32">Remarks</TableHead>
                        <TableHead className="w-24 text-center">Audited</TableHead>
                        <TableHead className="w-24">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {checklist.map((item) => (
                        <TableRow key={item.item_key} className={item.audited ? 'bg-green-50/50' : ''}>
                          <TableCell>{getStatusIcon(item.status)}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.item_name}</p>
                              {getStatusBadge(item.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-slate-600">
                              {item.details?.message && (
                                <p>{item.details.message}</p>
                              )}
                              {item.details?.total_consumption_value !== undefined && (
                                <p>Consumption: ₹{item.details.total_consumption_value.toLocaleString()}</p>
                              )}
                              {item.details?.net_sales !== undefined && (
                                <p>Net Sales: ₹{item.details.net_sales.toLocaleString()}</p>
                              )}
                              {item.details?.total_bills !== undefined && (
                                <p>Bills: {item.details.total_bills} (₹{item.details.total_amount?.toLocaleString()})</p>
                              )}
                              {item.details?.deposited !== undefined && (
                                <p>Deposited: ₹{item.details.deposited.toLocaleString()}</p>
                              )}
                              {item.details?.broken !== undefined && (
                                <p>Broken: {item.details.broken}, Lost: {item.details.lost}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.remarks ? (
                              <div className="flex items-center gap-1 text-sm">
                                <MessageSquare className="h-3 w-3 text-blue-500" />
                                <span className="truncate max-w-[100px]" title={item.remarks}>
                                  {item.remarks}
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={item.audited}
                              disabled={isSignedOff || (item.status !== 'ok' && !item.remarks)}
                              onCheckedChange={(checked) => {
                                if (item.status !== 'ok' && checked && !item.remarks) {
                                  setItemRemarks('');
                                  setRemarksDialog({ open: true, item });
                                } else {
                                  handleItemAudit(item, checked);
                                }
                              }}
                              data-testid={`audit-checkbox-${item.item_key}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isSignedOff}
                              onClick={() => {
                                setItemRemarks(item.remarks || '');
                                setRemarksDialog({ open: true, item });
                              }}
                              data-testid={`remarks-btn-${item.item_key}`}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Sign Off Section */}
                  {!isSignedOff && (
                    <div className="mt-6 pt-6 border-t flex justify-between items-center">
                      <div className="text-sm text-slate-500">
                        {canSignOff ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            All items reviewed. Ready to sign off.
                          </span>
                        ) : (
                          <span className="text-yellow-600 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            Review items with warnings/issues and add remarks before signing off.
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={() => setSignOffDialog(true)}
                        disabled={!canSignOff}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="sign-off-btn"
                      >
                        <FileCheck className="h-4 w-4 mr-2" />
                        Sign Off Audit
                      </Button>
                    </div>
                  )}

                  {isSignedOff && auditRecord && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800 mb-2">
                          <Lock className="h-4 w-4" />
                          <span className="font-medium">Audit Completed</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Signed off by <span className="font-medium">{auditRecord.signed_off_by_name}</span> on{' '}
                          {new Date(auditRecord.signed_off_at).toLocaleString()}
                        </p>
                        {auditRecord.overall_remarks && (
                          <p className="text-sm text-slate-600 mt-2">
                            <span className="font-medium">Remarks:</span> {auditRecord.overall_remarks}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Audit History</CardTitle>
              <CardDescription>View past audit records and sign-offs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signed Off By</TableHead>
                    <TableHead>Signed Off At</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No audit records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((audit) => (
                      <TableRow 
                        key={audit.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => {
                          setSelectedDate(audit.date);
                          setActiveTab('checklist');
                        }}
                      >
                        <TableCell className="font-medium">{audit.date}</TableCell>
                        <TableCell>
                          {audit.status === 'completed' ? (
                            <Badge className="bg-green-100 text-green-800">Completed</Badge>
                          ) : audit.status === 'in_progress' ? (
                            <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>{audit.signed_off_by_name || '—'}</TableCell>
                        <TableCell>
                          {audit.signed_off_at 
                            ? new Date(audit.signed_off_at).toLocaleString()
                            : '—'
                          }
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {audit.overall_remarks || '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Remarks Dialog */}
      <Dialog open={remarksDialog.open} onOpenChange={(open) => setRemarksDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Remarks</DialogTitle>
            <DialogDescription>
              {remarksDialog.item?.item_name} - {remarksDialog.item?.status === 'warning' || remarksDialog.item?.status === 'missing' 
                ? 'This item has issues. Please add remarks explaining the situation.'
                : 'Add optional remarks for this item.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {remarksDialog.item?.details?.message && (
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                {remarksDialog.item.details.message}
              </div>
            )}
            <Textarea
              placeholder="Enter your remarks..."
              value={itemRemarks}
              onChange={(e) => setItemRemarks(e.target.value)}
              rows={4}
              data-testid="remarks-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemarksDialog({ open: false, item: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRemarks}
              disabled={remarksDialog.item?.status !== 'ok' && !itemRemarks.trim()}
            >
              Save & Mark Audited
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Off Dialog */}
      <Dialog open={signOffDialog} onOpenChange={setSignOffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign Off Daily Audit</DialogTitle>
            <DialogDescription>
              Confirm that you have reviewed all items for {selectedDate}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Add overall remarks (optional)..."
              value={overallRemarks}
              onChange={(e) => setOverallRemarks(e.target.value)}
              rows={4}
              data-testid="overall-remarks-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOffDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSignOff} className="bg-green-600 hover:bg-green-700">
              <FileCheck className="h-4 w-4 mr-2" />
              Confirm Sign Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
